import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  useMediaQuery,
  Container,
  Drawer,
  Stack,
  Button,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import ThemeToggle from './common/ThemeToggle';
import acmAccLogo from '../assets/logos/acm-acc-logo-no-bg.svg';
import acmAccLogoDark from '../assets/logos/acm-acc-logo-no-bg-dark-mode.svg';
import { useThemeContext } from '../theme/ThemeContext';

const Header = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isRiverHackPage = location.pathname === '/riverhack';
  const { mode } = useThemeContext();
  const isDarkMode = mode === 'dark';

  const navLinks = [
    { text: 'Home', path: '/' },
    { text: 'About Us', path: '/about' },
    { text: 'RiverHacks', path: '/riverhacks', external: true },
    { text: 'Events', path: '/events' },
    { text: 'Sponsor', path: '/sponsor' },
  ];

  const scrollToFooter = (e: React.MouseEvent) => {
    e.preventDefault();
    const footer = document.querySelector('footer');
    if (footer) {
      footer.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const logo = isDarkMode ? acmAccLogoDark : acmAccLogo;

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: 'background.default',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              py: { xs: 2, md: 3 },
              px: { xs: 2, sm: 3, md: 4 },
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              minHeight: { xs: 64, md: 80 },
            }}
          >
            {/* Logo Section */}
            <Box 
              component={RouterLink} 
              to="/"
              sx={{ 
                height: { xs: 60, md: 70 },
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <img src={logo} alt="ACM ACC Logo" style={{ height: '100%' }} />
            </Box>

            {/* Desktop Navigation */}
            {!isMobile && (
              <>
                <Stack 
                  direction="row"
                  spacing={2}
                  sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                    mx: 4,
                  }}
                >
                  {navLinks.map((link) => (
                    <Button
                      key={link.path}
                      component={link.external ? 'a' : RouterLink}
                      to={!link.external ? link.path : undefined}
                      href={link.external ? link.path : undefined}
                      target={link.external ? '_blank' : undefined}
                      rel={link.external ? 'noopener noreferrer' : undefined}
                      sx={{
                        color: theme => theme.palette.mode === 'dark' ? '#c1d82f' : 'text.primary',
                        fontSize: '1.125rem',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        px: 1.5,
                        '&:hover': {
                          bgcolor: 'transparent',
                          opacity: 0.8,
                        },
                        '&:active': {
                          bgcolor: theme => alpha(theme.palette.mode === 'dark' ? '#c1d82f' : theme.palette.primary.main, 0.2),
                        },
                        ...(location.pathname === link.path && {
                          color: theme => theme.palette.mode === 'dark' ? '#c1d82f' : 'primary.main',
                        }),
                      }}
                    >
                      {link.text}
                    </Button>
                  ))}
                </Stack>
                {!isRiverHackPage && (
                  <Box sx={{ ml: 2 }}>
                    <ThemeToggle />
                  </Box>
                )}
              </>
            )}

            {/* Mobile Menu Icon */}
            {isMobile && (
              <IconButton
                onClick={() => setMobileOpen(true)}
                aria-label="Open navigation menu"
                aria-expanded={mobileOpen}
                aria-controls="mobile-menu"
                sx={{ ml: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>

        {/* Mobile Menu */}
        <Drawer
          id="mobile-menu"
          anchor="right"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
          PaperProps={{
            sx: {
              width: { xs: '100%', sm: 'max-content', minWidth: 320 },
              p: 3,
              background: theme.palette.mode === 'light' 
                ? 'rgba(255, 255, 255, 0.85)'
                : 'rgba(18, 18, 18, 0.85)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              border: 'none',
              boxShadow: theme.palette.mode === 'light'
                ? '0 4px 30px rgba(0, 0, 0, 0.1)'
                : '0 4px 30px rgba(0, 0, 0, 0.3)',
              height: 'auto',
              maxHeight: '100%',
              top: 0,
              bottom: 'auto',
              borderRadius: { xs: '0 0 0 16px', sm: '16px 0 0 16px' },
            },
          }}
        >
          <Stack spacing={4} sx={{ alignItems: 'center' }}>
            {/* Drawer Header */}
            <Box sx={{ 
              width: '100%',
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <Box
                component={RouterLink}
                to="/"
                onClick={() => setMobileOpen(false)}
                sx={{ height: 50 }}
              >
                <img src={logo} alt="ACM ACC Logo" style={{ height: '100%' }} />
              </Box>
              <IconButton 
                onClick={() => setMobileOpen(false)}
                aria-label="Close navigation menu"
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Navigation Links */}
            <Stack spacing={2} sx={{ width: '100%', alignItems: 'center' }}>
              {navLinks.map((link) => (
                <Button
                  key={link.path}
                  component={link.external ? 'a' : RouterLink}
                  to={!link.external ? link.path : undefined}
                  href={link.external ? link.path : undefined}
                  target={link.external ? '_blank' : undefined}
                  rel={link.external ? 'noopener noreferrer' : undefined}
                  onClick={() => setMobileOpen(false)}
                  fullWidth
                  sx={{
                    justifyContent: 'center',
                    color: theme => {
                      if (theme.palette.mode === 'dark') {
                        return location.pathname === link.path ? '#c1d82f' : '#c1d82f';
                      }
                      return location.pathname === link.path ? 'primary.main' : 'text.primary';
                    },
                    fontSize: '1.125rem',
                    fontWeight: 600,
                    py: 1.5,
                    borderRadius: 1,
                    '&:hover': {
                      bgcolor: theme => alpha(theme.palette.mode === 'dark' ? '#c1d82f' : theme.palette.primary.main, 0.04),
                    },
                    '&:active': {
                      bgcolor: theme => alpha(theme.palette.mode === 'dark' ? '#c1d82f' : theme.palette.primary.main, 0.2),
                    },
                  }}
                >
                  {link.text}
                </Button>
              ))}

              {/* Contact Button */}
              <Button
                onClick={(e) => {
                  scrollToFooter(e);
                  setMobileOpen(false);
                }}
                fullWidth
                sx={{
                  justifyContent: 'center',
                  color: theme => theme.palette.mode === 'dark' ? '#c1d82f' : 'text.primary',
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  py: 1.5,
                  borderRadius: 1,
                  '&:hover': {
                    bgcolor: theme => alpha(theme.palette.mode === 'dark' ? '#c1d82f' : theme.palette.primary.main, 0.04),
                  },
                  '&:active': {
                    bgcolor: theme => alpha(theme.palette.mode === 'dark' ? '#c1d82f' : theme.palette.primary.main, 0.2),
                  },
                }}
              >
                Contact
              </Button>

              {/* Theme Toggle for Mobile */}
              {!isRiverHackPage && (
                <Box sx={{ mt: 2 }}>
                  <ThemeToggle />
                </Box>
              )}
            </Stack>
          </Stack>
        </Drawer>
      </AppBar>
    </>
  );
};

export default Header;
